import { useState } from "react";
import { App, Button, Form, Input, InputNumber, Popconfirm, Select, Space, Table, Typography } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ChannelAffiliateConfig } from "@glow-price/shared";
import { ENTITY_STATUSES } from "@glow-price/shared";

import {
  affiliateNetworksApi,
  channelAffiliateConfigsApi,
  channelsApi,
} from "../api/resources";
import { CrudDrawer } from "../components/CrudDrawer";

const STATUS_OPTIONS = Object.values(ENTITY_STATUSES).map((v) => ({ value: v, label: v }));

export function ChannelAffiliateConfigsPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<ChannelAffiliateConfig | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const channelsQuery = useQuery({
    queryKey: ["channels", "all"],
    queryFn: () => channelsApi.list({ page: 1, pageSize: 200 }),
  });
  const networksQuery = useQuery({
    queryKey: ["affiliate-networks", "all"],
    queryFn: () => affiliateNetworksApi.list({ page: 1, pageSize: 200 }),
  });
  const channelOptions = (channelsQuery.data?.items ?? []).map((c) => ({
    value: c.id,
    label: c.name,
  }));
  const networkOptions = (networksQuery.data?.items ?? []).map((n) => ({
    value: n.id,
    label: `${n.name} (${n.code})`,
  }));

  const list = useQuery({
    queryKey: ["channel-affiliate-configs", page],
    queryFn: () => channelAffiliateConfigsApi.list({ page, pageSize: 20 }),
  });

  const saveMutation = useMutation({
    mutationFn: async (values: Partial<ChannelAffiliateConfig>) => {
      if (editing) return channelAffiliateConfigsApi.update(editing.id, values);
      return channelAffiliateConfigsApi.create(values);
    },
    onSuccess: () => {
      message.success("已保存");
      qc.invalidateQueries({ queryKey: ["channel-affiliate-configs"] });
      setDrawerOpen(false);
      setEditing(null);
    },
    onError: (err: Error) => message.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => channelAffiliateConfigsApi.remove(id),
    onSuccess: () => {
      message.success("已删除");
      qc.invalidateQueries({ queryKey: ["channel-affiliate-configs"] });
    },
    onError: (err: Error) => message.error(err.message),
  });

  return (
    <div>
      <Space style={{ marginBottom: 16, justifyContent: "space-between", width: "100%" }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          渠道联盟配置
        </Typography.Title>
        <Button
          type="primary"
          onClick={() => {
            setEditing(null);
            setDrawerOpen(true);
          }}
        >
          新建配置
        </Button>
      </Space>

      <Table<ChannelAffiliateConfig>
        rowKey="id"
        loading={list.isLoading}
        dataSource={list.data?.items ?? []}
        pagination={{
          current: page,
          pageSize: 20,
          total: list.data?.total ?? 0,
          onChange: (p) => setPage(p),
          showSizeChanger: false,
        }}
        columns={[
          {
            title: "渠道",
            dataIndex: "channelId",
            render: (v) => channelOptions.find((o) => o.value === v)?.label ?? v,
          },
          {
            title: "联盟",
            dataIndex: "networkId",
            render: (v) => networkOptions.find((o) => o.value === v)?.label ?? v,
          },
          { title: "AppId", dataIndex: "appId" },
          { title: "PID", dataIndex: "pid", width: 160 },
          {
            title: "佣金率",
            dataIndex: "commissionRate",
            width: 100,
            render: (v: number) => `${(v * 100).toFixed(2)}%`,
          },
          { title: "path 模板", dataIndex: "pathTemplate", ellipsis: true },
          {
            title: "操作",
            width: 160,
            render: (_, row) => (
              <Space>
                <Button
                  type="link"
                  onClick={() => {
                    setEditing(row);
                    setDrawerOpen(true);
                  }}
                >
                  编辑
                </Button>
                <Popconfirm title="确认删除？" onConfirm={() => deleteMutation.mutate(row.id)}>
                  <Button type="link" danger>
                    删除
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <CrudDrawer<Partial<ChannelAffiliateConfig>>
        open={drawerOpen}
        title={editing ? "编辑配置" : "新建配置"}
        initialValues={
          editing ?? {
            commissionRate: 0.04,
            status: ENTITY_STATUSES.ACTIVE,
            pathTemplate:
              "/pages/product/detail?id={{channelProductId}}&pid={{pid}}&click_id={{clickId}}",
          }
        }
        loading={saveMutation.isPending}
        onClose={() => {
          setDrawerOpen(false);
          setEditing(null);
        }}
        onSubmit={(values) => saveMutation.mutateAsync(values)}
      >
        {() => (
          <>
            <Form.Item name="channelId" label="渠道" rules={[{ required: true }]}>
              <Select options={channelOptions} />
            </Form.Item>
            <Form.Item name="networkId" label="联盟" rules={[{ required: true }]}>
              <Select options={networkOptions} />
            </Form.Item>
            <Form.Item name="appId" label="目标小程序 AppId" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="pathTemplate" label="path 模板" rules={[{ required: true }]}>
              <Input.TextArea rows={2} />
            </Form.Item>
            <Form.Item name="pid" label="PID">
              <Input placeholder="联盟分配的推广位 ID" />
            </Form.Item>
            <Form.Item name="subIdParam" label="sub_id 参数名">
              <Input placeholder="click_id / s2 / 自定义" />
            </Form.Item>
            <Form.Item name="commissionRate" label="佣金率">
              <InputNumber min={0} max={1} step={0.005} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="status" label="状态">
              <Select options={STATUS_OPTIONS} />
            </Form.Item>
          </>
        )}
      </CrudDrawer>
    </div>
  );
}
